import React, { useEffect, useState } from "react";
import { FluentProvider, webLightTheme, webDarkTheme } from "@fluentui/react-components";
import { useSelector } from "react-redux";

import { Backend } from "../lib/backend";
import { selectUser } from "../lib/scraper.slice";
import { IUser } from "../lib/types";
import { setTelemetryUser, clearTelemetryUser } from "../lib/telemetry";

import { OfficeAppProvider, useOfficeAppContext, Pages } from "./OfficeAppContext";
import { Loading } from "./components/Loading";
import { useOffice } from "../hooks/useOffice";
import { Dashboard } from "./screens/Dashboard";
import { EmailDetails } from "./screens/EmailDetails";

// office-specific alert component
const Alert = ({ message, type }: { message: string; type: "error" | "info" }) => (
    <div style={{
        padding: "1rem",
        backgroundColor: type === "error" ? "#f8d7da" : "#d1ecf1",
        color: type === "error" ? "#721c24" : "#0c5460",
        borderRadius: "4px",
        margin: "1rem 0"
    }}>
        {message}
    </div>
);

const OfficeAppContent = () => {
    const {
        current_page,
        error,
        is_authenticating: isAuthenticating,
        user: context_user,
        setIsAuthenticating,
        setUser,
        setError,
        setSelectedEmail,
        setCurrentPage,
        setIsLoading,
    } = useOfficeAppContext();

    const redux_user = useSelector(selectUser);
    const office = useOffice();
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        // only run initialization once when Office is ready
        if (initialized || !office.is_ready || office.error) {
            return;
        }

        if (office.error) {
            setError(`Office initialization failed: ${office.error}`);
            return;
        }

        setInitialized(true);
        setIsAuthenticating(true);

        const handleEmailChange = () => {
            const email_item = office.getCurrentEmail();
            if (email_item === null) {
                setCurrentPage(Pages.Dashboard);
                setSelectedEmail(undefined);
                return;
            }
            setSelectedEmail(email_item);
            setCurrentPage(Pages.EmailSelected);
        };

        const initializeApp = async () => {
            try {
                // use Redux user if available, otherwise fetch
                let final_user: IUser;
                if (redux_user) {
                    final_user = redux_user;
                } else {
                    const user_data = await Backend.getUser();
                    if (!user_data.user) {
                        setError("Authentication failed: No user data available");
                        return;
                    }
                    final_user = user_data.user;
                }

                setUser(final_user);

                // set initial page based on email context
                const current_email = office.getCurrentEmail();
                if (current_email) {
                    setSelectedEmail(current_email);
                    setCurrentPage(Pages.EmailSelected);
                } else {
                    setCurrentPage(Pages.Dashboard);
                }

                // set up email change handler
                const Office = (globalThis as any).Office;
                if (Office && Office.context && Office.context.mailbox) {
                    Office.context.mailbox.addHandlerAsync(
                        Office.EventType.ItemChanged,
                        handleEmailChange
                    );
                }

                // stop the context loading state so Dashboard can take over
                setIsLoading(false);
            } catch (error: any) {
                setError(`App initialization failed: ${error.message}`);
            } finally {
                setIsAuthenticating(false);
            }
        };

        initializeApp();
    }, [office.is_ready, initialized, office, redux_user, setCurrentPage, setError, setIsAuthenticating, setIsLoading, setSelectedEmail, setUser]);

    // handle Redux user updates (if user logs in/out in main app)
    useEffect(() => {
        if (initialized && redux_user && !context_user) {
            setUser(redux_user);
        }
    }, [redux_user, context_user, initialized, setUser]);

    // set telemetry user
    useEffect(() => {
        const current_user_uuid = context_user?.uuid || redux_user?.uuid;
        if (current_user_uuid) {
            setTelemetryUser(current_user_uuid);
        } else {
            clearTelemetryUser();
        }
    }, [context_user?.uuid, redux_user?.uuid]);

    // show loading state if Office is still initializing
    if (office.is_loading) {
        return <Loading text="Initializing..." />;
    }

    // show error state if Office failed to initialize
    if (office.error || error) {
        return (
            <div style={{ padding: "1rem" }}>
                <Alert
                    message={office.error || error || "Unknown error"}
                    type="error"
                />
            </div>
        );
    }

    if (isAuthenticating) {
        return <Loading text="Loading your Nordoon dashboard..." />;
    }

    if (current_page === Pages.EmailSelected) {
        return <EmailDetails />;
    }

    if (current_page === Pages.Dashboard) {
        return <Dashboard />;
    }

    return null;
};

export const OfficeApp = () => {
    const office = useOffice();
    const theme = office.is_ready ?
        (office.getOfficeTheme().is_dark_theme ? webDarkTheme : webLightTheme) :
        webLightTheme;

    return (
        <FluentProvider theme={theme}>
            <OfficeAppProvider>
                <OfficeAppContent />
            </OfficeAppProvider>
        </FluentProvider>
    );
};